import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { useToast } from '../context/ToastContext';

interface ChurchDetails {
    _id: string;
    name: string;
    branding?: {
        logoUrl?: string;
    };
}

interface ProtectedRouteProps {
    children: React.ReactNode;
    roles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const currentEnvChurchIdentifier = import.meta.env.VITE_APP_CHURCH_IDENTIFIER;

    const [currentChurch, setCurrentChurch] = useState<ChurchDetails | null>(() => {
        try {
            const storedChurch = sessionStorage.getItem('currentChurch');
            return storedChurch ? JSON.parse(storedChurch) : null;
        } catch (e) {
            console.error("Error parsing stored church details from sessionStorage, will refetch.", e);
            sessionStorage.removeItem('currentChurch');
            return null;
        }
    });

    const [isLoadingChurchDetails, setIsLoadingChurchDetails] = useState<boolean>(false);

    const applyChurchBranding = (church: ChurchDetails) => {
        if (church.name) {
            document.title = church.name;
            console.log("Page title updated to:", church.name);
        } else {
            console.warn("Missing 'church.name'. Page title not updated.");
        }

        if (church.branding && church.branding.logoUrl) {
            let faviconLink: HTMLLinkElement | null = document.querySelector("link[rel='icon']");

            if (!faviconLink) {
                faviconLink = document.createElement('link') as HTMLLinkElement;
                faviconLink.rel = 'icon';
                document.head.appendChild(faviconLink);
            }
            faviconLink.href = church.branding.logoUrl;
            console.log("Favicon updated to:", church.branding.logoUrl);
        } else {
            console.warn("Missing 'church.branding' or 'church.branding.logoUrl'. Favicon not updated.");
        }
    };

    useEffect(() => {
        const fetchAndSetChurchDetails = async () => {
            if (currentChurch) {
                applyChurchBranding(currentChurch);
                return;
            }

            console.log("No 'currentChurch' data found in state/sessionStorage. Attempting to fetch from API...");
            setIsLoadingChurchDetails(true);
            try {
                const response = await axiosInstance.get<ChurchDetails>(`/churches/${currentEnvChurchIdentifier}`);
                const fetchedChurch = response.data;

                if (fetchedChurch) {
                    setCurrentChurch(fetchedChurch);
                    sessionStorage.setItem('currentChurch', JSON.stringify(fetchedChurch));
                    applyChurchBranding(fetchedChurch);
                    //showToast('Church details loaded successfully.', 'success');
                } else {
                    console.warn("API returned no church details for the identifier.");
                    showToast('Failed to load church details for this environment.', 'error');
                }
            } catch (error) {
                console.error("Failed to fetch church details from API:", error);
                showToast('Error fetching church details. Please try again.', 'error');
            } finally {
                setIsLoadingChurchDetails(false);
            }
        };

        fetchAndSetChurchDetails();
    }, [currentChurch, showToast]);

    useEffect(() => {
        if (!user) {
            console.log("ProtectedRoute: User not found, redirecting to /login");
            navigate('/login', { replace: true });
            return;
        }

        if (roles && !roles.includes(user.role)) {
            console.log(`ProtectedRoute: User role '${user.role}' not authorized, redirecting to /dashboard`);
            navigate('/dashboard', { replace: true });
            const messageDiv = document.createElement('div');
            messageDiv.className = 'fixed top-4 right-4 bg-red-500 text-white p-3 rounded-md shadow-lg z-50';
            messageDiv.innerText = `You do not have the required role to access this page. Your role is: ${user.role}`;
            document.body.appendChild(messageDiv);
            setTimeout(() => {
                document.body.removeChild(messageDiv);
            }, 3000);
            return;
        }

        if (currentChurch && currentEnvChurchIdentifier && user.churchId && user.churchId.toString() !== currentEnvChurchIdentifier) {
            console.warn(`ProtectedRoute: User's churchId '${user.churchId}' does not match environment church ID '${currentEnvChurchIdentifier}'. Logging out.`);
            logout();
            return;
        }

    }, [user, roles, navigate, currentEnvChurchIdentifier, logout, currentChurch]);

    if (isLoadingChurchDetails) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <svg className="animate-spin h-10 w-10 text-indigo-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-gray-700 text-lg">Loading application details...</p>
                </div>
            </div>
        );
    }

    if (!currentChurch) {
        return null;
    }

    if (user && (!roles || roles.includes(user.role)) && user.churchId.toString() === currentEnvChurchIdentifier) {
        return <>{children}</>;
    }

    return null;
};

export default ProtectedRoute;

